import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { BrandingConfigurationService } from '@ptl/brand';
import { CountyListResponse, CountyListService, PtlWindowLocationService } from '@ptl/global-components';
import {
  HTTPWrapperContext,
  RequestConfigImplementation,
  REQUEST_METHODS,
  XHR_STRATEGIES
} from '@ptl/xhr';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { GlobalDataStateStructure } from '../../shared/components/configs/interfaces/globalStateStructure.interface';
import { GLOBAL_DATA_REDUCER_NAME } from '../../shared/components/configs/reducers/globalData.reducer';
import { PROSPECT_API_ENUM } from '../enums/prospect-api.enum';
import { ProspectQueryParams, ProspectServiceParams } from '../interfaces/prospect-query-params.interface';
import { CreateProspectRequest, Prospect, ProspectResponse, UpdateProspectStatusRequest } from '../interfaces/prospect.interface';
import { NormalizedResponse } from '@ptl/xhr';
import { OnExchangeProspectRetrievalProducer, Prospect as ProducerRetrievedProspect } from 'src/app/angular-elements/on-exchange-enrollment/interfaces/on-exchange-prospects-retrieval.producer.response.interface';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';

/** Blank App - Coming referral email / link-sharing - View-Plans click enroll */

// populateBlankAppPropsectRequest = function(emailProducerData, familyData, planData) {

//   var prospect = lang.clone(blankAppProspectRequest);
//   prospect.firstName = familyData.groups[0].persons[0].fullName.firstName;
//   prospect.dob = new Date(familyData.groups[0].persons[0].dob).getTime();
//   prospect.zipCode = familyData.zipCode;
//   prospect.state = familyData.state;
//   prospect.county = familyData.county;
//   prospect.agent.agencyId = emailProducerData.agencyID;
//   prospect.agent.agentId = emailProducerData.agentID;
//   prospect.agent.linkShareKey = decodeURIComponent(emailProducerData.urlKey);

//   if (planData) {
//       prospect.prospectBYFInfo.selectedPlan = planData;
//   }
//   return {
//       payload: {
//           prospect: prospect
//       }
//   };
// },

/** Existing Prospect - Coming from referral email / link-sharing - Authorization post-submit */
// populateBlankAppUpdateProspectRequest = function (emailProducerData, familyData, prospectInfo) {

//   var prospect = lang.clone(blankAppProspectUpdateRequest);
//   var subscriberData = familyData[0];

//   prospect.prospectId = prospectInfo.prospectId;
//   prospect.applicationType = "OFFEX";
//   prospect.firstName = subscriberData.fullName.firstName;
//   prospect.middleName = subscriberData.fullName.middleName;
//   prospect.lastName = subscriberData.fullName.lastName;
//   prospect.dob = new Date(subscriberData.dateOfBirth).getTime();
//   prospect.address1 = subscriberData.homeAddress.addressLine1;
//   prospect.address2 = subscriberData.homeAddress.addressLine2;
//   prospect.city = subscriberData.homeAddress.city;
//   prospect.state = subscriberData.homeAddress.state;
//   prospect.zipCode = subscriberData.homeAddress.zipCode;
//   prospect.county = subscriberData.homeAddress.county;
//   prospect.homePhone = subscriberData.primaryPhoneNumber;
//   prospect.cellPhone = subscriberData.cellNumber;
//   prospect.busPhone = subscriberData.otherPhoneNumber;
//   prospect.email = subscriberData.email;
//   prospect.agent.agencyId = emailProducerData.agencyID;
//   prospect.agent.agentId = emailProducerData.agentID;
//   prospect.agent.linkShareKey = decodeURIComponent(emailProducerData.urlKey);

//   return {
//       payload: {
//           prospect: prospect
//       }
//   };
// },

/**
 * /public APIs are used for a consumer, accessing prospect data from link-sharing
 * /prospect APIs are used for logged in producers
 * /prospectupdate API is used during an active flow with a prospectId to update the status of flow navigation, separate from the app itself
 *    - this one can use the prospect-update.interface (maybe this changes names after 9/1)
 */
@Injectable({
  providedIn: 'root'
})
export class ProspectService {

  forceValues = false;

  constructor(
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext,
    private windowLocationService: PtlWindowLocationService,
    private reducerUtilityService: ReducerUtilityService,
    private commonUtilityService: CommonUtilityService,
    private countyListService: CountyListService
  ) {}

  async getProspectData(): Promise<ProspectResponse> {
    const payload = {
      npn: '',
      agencyId: ''
    };
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: PROSPECT_API_ENUM.GET_PROSPECT,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);

    return this.httpClient.makeRequest<ProspectResponse>(requestConfigImplementation);
  }

  createProspect(producerGeneratedProspect?: Prospect, selectedMarket?: LINE_OF_BUSINESS_ENUM): Promise<ProspectResponse> {
    const loginInfoState: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    /** Build a blank prospect object */
    const getProspectPayloadFromUserInfo = (): string => {

      const prospect: Prospect = {
        firstName: loginInfoState.userInfo.firstName,
        lastName: loginInfoState.userInfo.lastName,
        dob: new Date(loginInfoState.userInfo.dob).getTime(),
        address1: loginInfoState.userInfo.address && loginInfoState.userInfo.address[0], // does not exist on userInfo response
        address2: loginInfoState.userInfo.address && loginInfoState.userInfo.address[1], // does not exist on userInfo response
        shoppingInfo: {
            brand: loginInfoState.userInfo.shoppingBrand,
            zipCode: loginInfoState.userInfo.shoppingZipCode ? loginInfoState.userInfo.shoppingZipCode : loginInfoState.userInfo.zipCode
        },
        city: loginInfoState.userInfo.city,
        state: loginInfoState.userInfo.state,
        zipCode: loginInfoState.userInfo.zipCode,
        email: loginInfoState.userInfo.email,
        applicationType: 'ONEX'
      };
      return JSON.stringify(new CreateProspectRequest(prospect));
    };

    /** Producer will pass data, Consumer will have data on their login state */
    const payload: string = ((): string => {
      if (this.commonUtilityService.isAgent() && producerGeneratedProspect) {

        const agent = {
          npn: loginInfoState.userInfo.npnNo,
          ffmId: loginInfoState.userInfo.ffmId || null, // User Id that shows up at ffm login
          firstName: loginInfoState.userInfo.firstName || '',
          lastName: loginInfoState.userInfo.lastName || '',
          agencyId: loginInfoState.userInfo.agencyInfo.selectedAgencies[selectedMarket].agencyId || ''
        };

        const status = {
            id: 6,
            description: 'New Customer',
            details: 'brand new customer'
        };

        producerGeneratedProspect.agent = agent;
        producerGeneratedProspect.status = status;
        return JSON.stringify(new CreateProspectRequest(producerGeneratedProspect));
      } else {
        return getProspectPayloadFromUserInfo();
      }
    })();

    const headers = {
      ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
    }
    if (this.commonUtilityService.isAgent()) {
      headers['FromCSR'] = loginInfoState.userId;
    }
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.PUT,
      url: PROSPECT_API_ENUM.PROSPECT,
      headers: new HttpHeaders(headers),
      POST_OR_PUT_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<ProspectResponse>(requestConfigImplementation);
  }

  updateProspectStatus(updateProspectRequestPayload: UpdateProspectStatusRequest): Promise<any> {

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: PROSPECT_API_ENUM.PROSPECT,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: updateProspectRequestPayload.getAsStringifiedPayload()
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<ProspectResponse>(requestConfigImplementation);
  }
  updateProspectStatusAsProducer(updateProspectRequestPayload: UpdateProspectStatusRequest): Promise<any> {

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: PROSPECT_API_ENUM.PROSPECT_STATUS,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: {
        baseMessage: {headerVersion: '1.0'},
        payload: updateProspectRequestPayload.payload
      }
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<ProspectResponse>(requestConfigImplementation);
  }

  individualUpdateOnExchangeStatus(payload: ProspectServiceParams) {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: PROSPECT_API_ENUM.INDIVIDUAL_UPDATE_ON_EXCHANGE_STATUS,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: { payload: payload }
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<unknown>(requestConfigImplementation);
  }

  /** If there are query params relevant to this flow, they will be used to trigger an update request for the active prospect */
  async parseServiceParams() {
    // const queryParams = this.windowLocationService.getParsedQueryParams<ProspectQueryParams>();
    const globalDataState: GlobalDataStateStructure
      = this.reducerUtilityService.getComponentStates()[GLOBAL_DATA_REDUCER_NAME];

    let flagCheck = false;

    /** Reference the keys from the expected query param response ( previously stored in state) - if any exist, CMS flow */
    const CMS_KEYS = [
      'P1',
      'P2',
      'P3',
      'P4',
      'P5',
      'P6',
      'P7'
    ];
    for (const keyA in CMS_KEYS) {
      if (globalDataState.urlParams.hasOwnProperty(CMS_KEYS[keyA])) {
        flagCheck = true;
      }
    }

    if (flagCheck) {

      const serviceParams: ProspectServiceParams = {};

      for (const key in CMS_KEYS) {

        if (globalDataState.urlParams.hasOwnProperty(CMS_KEYS[key])) {

          const newValue = window.atob(globalDataState.urlParams[CMS_KEYS[key]]);

          switch (CMS_KEYS[key]) {
            case 'P1': serviceParams.InformationExchangeSystemID = newValue; break;
            case 'P2': serviceParams.StateExchangeCode = newValue; break;
            case 'P3': serviceParams.PartnerAssignedConsumerID = newValue; break;
            case 'P4': serviceParams.FFEAssignedConsumerID = newValue; break;
            case 'P5': serviceParams.UserType = newValue; break;
            case 'P6': serviceParams.FFEUserID = newValue; console.log(newValue + ' this is the ffmAssignedConsumerID which we can send to CMS'); break;
            case 'P7': serviceParams.TransferType = newValue; break;
            case 'P8': serviceParams.ExceptionReason = newValue; break;
          }
        }
      }

      const individualUpdateOnExchangeStatusResponse = await this.individualUpdateOnExchangeStatus(serviceParams);
      return individualUpdateOnExchangeStatusResponse;
    }

  }

  async createProspectIfDoesNotExist(): Promise<Prospect[]> {
    const doesProspectExist: ProspectResponse = await this.getProspectData();

    if (doesProspectExist instanceof HttpErrorResponse) {
      return null;
    }

    if (doesProspectExist?.payload?.prospects?.length > 0) {
      /** Prospect exists, no need to create */
      return doesProspectExist.payload.prospects;
    } else {
      /**
       * @TODO - if this createProspect request returns the prospect, we can drop the subsequent GET
       */
      await this.createProspect();
      const createdProspect: ProspectResponse = await this.getProspectData();
      return createdProspect.payload.prospects;
    }
  }

  async getProspectListProducer(market: LINE_OF_BUSINESS_ENUM, applicationStatus: 'IN-PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'EMAILED' | 'ARCHIVED') {

    const url = (() => {
      switch (applicationStatus) {
        case 'IN-PROGRESS': return PROSPECT_API_ENUM.PRODUCER_ACTIVE_APPS;
        case 'COMPLETED': return PROSPECT_API_ENUM.PRODUCER_COMPLETED_APPS;
        case 'EXPIRED': return PROSPECT_API_ENUM.PRODUCER_EXPIRED_APPS;
        case 'EMAILED': return PROSPECT_API_ENUM.PROUDUCER_EMAILED_APPS;
        case 'ARCHIVED': return PROSPECT_API_ENUM.PROUDUCER_ARCHIVED_APPS;
      }
    })();

    const loginInfoState: UserInfoStateStructure
      = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const selectedAgency = loginInfoState.userInfo.agencyInfo.selectedAgencies[market];

    const hasNpn = !!loginInfoState?.userInfo?.npnNo;

    const getPayload
      = hasNpn
        ? { agencyId: selectedAgency.agencyId, npn: loginInfoState.userInfo.npnNo }
        : { agencyId: selectedAgency.agencyId };

    if (applicationStatus === 'EMAILED') {
      delete getPayload.npn;
    }

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      GET_PAYLOAD: getPayload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);

    const response = await this.httpClient.makeRequest<NormalizedResponse<OnExchangeProspectRetrievalProducer>>(requestConfigImplementation);

    return response.payload.prospects.filter(p => p.applicationType === 'ONEX');
  }

  /**
   * Use the shopping data to generate branding information - since we don't do the zipCounty modal, we want to align
   * with the user shopping data
   * 1) Consumer prospect retrieved from self-/prospect service
   * 2) Producer prospect retrieved either from: /prospect-status or /eligibility ( as when returning directly from CMS )
   */
  async configureBrandingFromProspectResponse(
    prospect: Prospect | ProducerRetrievedProspect | { zipCode: string | number, county: string }
  ) {

    const prospectShoppingInfo = (prospect as Prospect).shoppingInfo;
    const producerProspectShoppingInfo = { zipCode: prospect.zipCode, county: prospect.county };

    let shoppingInfo = null;
    if (prospectShoppingInfo?.zipCode && prospectShoppingInfo?.county) {
      shoppingInfo = prospectShoppingInfo;
    } else if (producerProspectShoppingInfo?.zipCode && producerProspectShoppingInfo?.county) {
      shoppingInfo = producerProspectShoppingInfo;
    } else {
      throw new Error('Branding not working as intended, this will break view-plans');
    }

    let zipForCountyList = shoppingInfo.zipCode;
    if (shoppingInfo.zipCode.length > 5) {
      zipForCountyList = shoppingInfo.zipCode.split('-')[0];
    }

    const response: NormalizedResponse<CountyListResponse>
      = await this.countyListService.getCountyInformation(zipForCountyList);

    const counties = response.payload.countyList;
    let countyFinal = null;
    if (counties.length === 1) {
      countyFinal = counties[0];
    } else {
      const filteredCounty = counties.filter(c => c.county === shoppingInfo.county);
      if (filteredCounty && filteredCounty.length > 0) {
        countyFinal = filteredCounty[0];
      }
    }

    this.brandService.setBrandCookie(countyFinal.brand);
    this.brandService.setDdTokenCookie(countyFinal.brand);
    this.commonUtilityService.saveCountyZipData(countyFinal, zipForCountyList);

  }
}
